import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { FiEdit2, FiEye, FiPlus, FiSearch, FiTrash2, FiX } from 'react-icons/fi';
import Layout from '../components/Layout';
import Pagination from '../components/Pagination';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import {
    formatSingaporeDateDmy,
    formatSingaporeDateTimeDmyAmPm,
    toSingaporeDatetimeLocalValue,
    toSingaporeDateInputValue,
} from '../utils/singaporeTime';

const DEFAULT_PLATFORMS = ['godReview', 'telegram', 'google', 'other'];
const DEFAULT_REVIEW_TYPES = ['Massage Review'];

const emptyForm = () => ({
    linkType: 'SupermanMember',
    linkedId: '',
    paymentDate: '',
    paymentAmount: '',
    paymentRemarks: '',
    reviewCount: '4',
    intervalDays: '7',
    startDate: '',
    platforms: ['godReview'],
    status: 'active',
});

const emptyDeliveryForm = () => ({
    reviewComment: '',
    sender: '',
    type: 'Massage Review',
    platforms: [{ platform: 'godReview', dateTime: '' }],
    remarks: '',
});

const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-md text-blue-700 font-bold';

const formatGodMemberLabel = (m) => {
    if (!m) return '';
    return [m.godID || '—', m.godName || '—', m.webID || '—'].join(' - ');
};

const formatSupermanMemberLabel = (m) => {
    if (!m) return '';
    return `${m.name || '—'} (${m.webID || 'no webID'})`;
};

const cleanPlatforms = (list) => [
    ...new Set((list || []).map((v) => String(v || '').trim()).filter(Boolean)),
];

/** Normalize delivery platforms to { platform, dateTime } for the complete/edit form. Empty dateTime = not done yet. */
const toDeliveryPlatformRows = (platforms, fallbackNames = ['godReview']) => {
    const raw = Array.isArray(platforms) ? platforms : [];
    const rows = raw.map((entry) => {
        if (typeof entry === 'string') {
            return { platform: entry, dateTime: '' };
        }
        if (entry && typeof entry === 'object') {
            return {
                platform: String(entry.platform || entry.name || '').trim(),
                dateTime: toSingaporeDatetimeLocalValue(entry.dateTime) || '',
            };
        }
        return null;
    }).filter((row) => row && row.platform);

    if (rows.length) return rows;
    return (fallbackNames.length ? fallbackNames : ['godReview']).map((platform) => ({
        platform,
        dateTime: '',
    }));
};

const cleanDeliveryPlatforms = (list) => {
    const seen = new Set();
    const rows = [];
    for (const entry of list || []) {
        const platform = String(entry?.platform || '').trim();
        if (!platform || seen.has(platform)) continue;
        seen.add(platform);
        rows.push({
            platform,
            dateTime: entry.dateTime || null,
        });
    }
    return rows.length ? rows : [{ platform: 'godReview', dateTime: null }];
};

const areDeliveryPlatformsComplete = (platforms) => {
    const rows = Array.isArray(platforms) ? platforms : [];
    if (!rows.length) return false;
    return rows.every((row) => String(row?.platform || '').trim() && row?.dateTime);
};

/** True when any platform is missing a dateTime (or no platforms). */
const deliveryHasEmptyPlatformDate = (platforms) => {
    const rows = Array.isArray(platforms) ? platforms : [];
    if (!rows.length) return true;
    if (typeof rows[0] === 'string') return true;
    return rows.some((row) => {
        const name = String(row?.platform || row?.name || '').trim();
        if (!name) return true;
        return !row?.dateTime;
    });
};

/** View modal: one platform per line as `platform (dd/mm/yy h:mm am/pm)`. */
const formatDeliveryPlatforms = (platforms) => {
    if (!Array.isArray(platforms) || !platforms.length) return '—';
    if (typeof platforms[0] === 'string') {
        return platforms.map((name) => String(name || '').trim()).filter(Boolean).join('\n') || '—';
    }
    return platforms.map((row) => {
        const name = String(row?.platform || row?.name || '').trim() || '—';
        if (!row?.dateTime) return name;
        return `${name} (${formatSingaporeDateTimeDmyAmPm(row.dateTime)})`;
    }).join('\n');
};

/**
 * Action suffix for delivery Edit button (remarks excluded).
 * Done = all fields + every platform date filled
 * Pending = all fields filled but at least one platform date empty
 * "" = any required field empty
 */
const getDeliveryEditValue = (delivery) => {
    if (!delivery) return '';
    const sender = delivery.sender?._id || delivery.sender || '';
    const type = String(delivery.type || '').trim();
    const comment = String(delivery.reviewComment || '').trim();
    const rawPlatforms = Array.isArray(delivery.platforms) ? delivery.platforms : [];
    const platforms = rawPlatforms.map((entry) => {
        if (typeof entry === 'string') return { platform: entry.trim(), dateTime: null };
        return {
            platform: String(entry?.platform || entry?.name || '').trim(),
            dateTime: entry?.dateTime || null,
        };
    }).filter((row) => row.platform);

    const fieldsFilled = Boolean(sender) && Boolean(type) && Boolean(comment) && platforms.length > 0;
    if (!fieldsFilled) return '';

    const allDatesFilled = platforms.every((row) => row.dateTime);
    return allDatesFilled ? 'Done' : 'Pending';
};

const deliveryRowClass = (delivery) => (
    deliveryHasEmptyPlatformDate(delivery?.platforms)
        ? 'bg-red-100 hover:bg-red-200'
        : 'hover:bg-gray-50'
);

const deliveryEditStatusClass = (value) => {
    if (value === 'Done') return 'text-blue-700 font-semibold';
    if (value === 'Pending') return 'text-amber-700 font-semibold';
    return '';
};

const formatLinkedMember = (payment) => {
    if (payment?.SupermanMember) {
        const m = payment.SupermanMember;
        return `Superman: ${m.name || m.webID || m._id}`;
    }
    if (payment?.godMember) {
        const m = payment.godMember;
        return `God: ${formatGodMemberLabel(m)}`;
    }
    return '—';
};

const formatArmyLabel = (army) => {
    if (!army) return '-';
    const name = army.name || '-';
    const username = army.tgusername
        ? `@${String(army.tgusername).replace(/^@/, '')}`
        : '';
    return username ? `${name} - ${username}` : name;
};

const armyMatchesSearch = (army, term) => {
    if (!term) return true;
    const q = term.toLowerCase().replace(/^@/, '');
    const hay = [
        army.name,
        army.number,
        army.tgid,
        army.tgusername,
        army.tgusername ? `@${String(army.tgusername).replace(/^@/, '')}` : '',
    ].filter(Boolean).join(' ').toLowerCase();
    return hay.includes(q);
};

const statusClass = (status) => {
    if (status === 'completed') return 'text-green-700 font-semibold';
    if (status === 'cancelled') return 'text-red-600 font-semibold';
    return 'text-amber-700 font-semibold';
};

/**
 * Active = light yellow.
 * Active + next scheduled review due today/overdue (SG) and not fully done = light red.
 */
const paymentRowClass = (row) => {
    const status = row?.status || 'active';
    if (status !== 'active') return 'hover:bg-gray-50';

    const dueKey = toSingaporeDateInputValue(row?.nextReviewDate);
    const todayKey = toSingaporeDateInputValue(new Date());
    const overdue = Boolean(dueKey && todayKey && dueKey <= todayKey);
    if (overdue && Number(row?.reviewBal || 0) > 0) {
        return 'bg-red-100 hover:bg-red-200';
    }
    return 'bg-yellow-100 hover:bg-yellow-200';
};

const SupermanPaymentPage = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [memberTypeFilter, setMemberTypeFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [selected, setSelected] = useState(null);
    const [deliveries, setDeliveries] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [formMode, setFormMode] = useState('create');
    const [formData, setFormData] = useState(emptyForm());
    const [formLoading, setFormLoading] = useState(false);
    const [showView, setShowView] = useState(false);
    const [viewLoading, setViewLoading] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [pastPlatforms, setPastPlatforms] = useState(DEFAULT_PLATFORMS);
    const [reviewTypes, setReviewTypes] = useState(DEFAULT_REVIEW_TYPES);
    const [supermanArmies, setSupermanArmies] = useState([]);
    const [memberSearchTerm, setMemberSearchTerm] = useState('');
    const [memberSearchResults, setMemberSearchResults] = useState([]);
    const [memberSearchLoading, setMemberSearchLoading] = useState(false);
    const [showMemberDropdown, setShowMemberDropdown] = useState(false);
    const [showComplete, setShowComplete] = useState(false);
    const [completeTarget, setCompleteTarget] = useState(null);
    const [deliveryForm, setDeliveryForm] = useState(emptyDeliveryForm());
    const [completeLoading, setCompleteLoading] = useState(false);
    const [senderSearchTerm, setSenderSearchTerm] = useState('');
    const [showSenderDropdown, setShowSenderDropdown] = useState(false);

    const authHeaders = () => ({
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'ngrok-skip-browser-warning': 'true',
    });

    const fetchItems = useCallback(async (page = 1, search = '', memberType = '', status = '') => {
        try {
            setLoading(true);
            let url = `${process.env.REACT_APP_API_URL}/api/supermanpayments?page=${page}&limit=20&sort=-paymentDate`;
            if (search) url += `&search=${encodeURIComponent(search)}`;
            if (memberType) url += `&memberType=${encodeURIComponent(memberType)}`;
            if (status) url += `&status=${encodeURIComponent(status)}`;
            const res = await axios.get(url, { headers: authHeaders() });
            if (res.data.success) {
                setItems(res.data.data);
                setTotal(res.data.pagination.total);
                setTotalPages(res.data.pagination.pages);
            }
        } catch (err) {
            setError(`Failed to fetch payments: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchDeliveryOptions = useCallback(async () => {
        try {
            const headers = authHeaders();
            const [platformRes, typeRes, armyRes] = await Promise.all([
                axios.get(`${process.env.REACT_APP_API_URL}/api/supermanpayments/platforms`, { headers }),
                axios.get(`${process.env.REACT_APP_API_URL}/api/supermandeliveries/types`, { headers }),
                axios.get(`${process.env.REACT_APP_API_URL}/api/supermanarmies?page=1&limit=500&sort=name`, { headers }),
            ]);
            if (platformRes.data.success) {
                setPastPlatforms(
                    platformRes.data.data?.length ? platformRes.data.data : DEFAULT_PLATFORMS
                );
            }
            if (typeRes.data.success) {
                setReviewTypes(
                    typeRes.data.data?.length ? typeRes.data.data : DEFAULT_REVIEW_TYPES
                );
            }
            if (armyRes.data.success) setSupermanArmies(armyRes.data.data || []);
        } catch {
            // options are best-effort for the form
        }
    }, []);

    const searchMembers = useCallback(async (term, linkType) => {
        const clean = String(term || '').trim();
        if (!clean) {
            setMemberSearchResults([]);
            return;
        }
        setMemberSearchLoading(true);
        try {
            const headers = authHeaders();
            if (linkType === 'godMember') {
                const res = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/godmembers/search?term=${encodeURIComponent(clean)}`,
                    { headers }
                );
                if (res.data.success) setMemberSearchResults(res.data.data || []);
            } else {
                const res = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/supermanmembers?page=1&limit=20&sort=name&search=${encodeURIComponent(clean)}`,
                    { headers }
                );
                if (res.data.success) setMemberSearchResults(res.data.data || []);
            }
        } catch {
            setMemberSearchResults([]);
        } finally {
            setMemberSearchLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchItems(currentPage, searchTerm, memberTypeFilter, statusFilter);
    }, [currentPage, searchTerm, memberTypeFilter, statusFilter, fetchItems]);

    useEffect(() => {
        fetchDeliveryOptions();
    }, [fetchDeliveryOptions]);

    useEffect(() => {
        if (!showForm || formData.linkedId) return undefined;
        const term = memberSearchTerm.trim();
        if (!term) {
            setMemberSearchResults([]);
            return undefined;
        }
        const timer = setTimeout(() => {
            searchMembers(term, formData.linkType);
        }, 300);
        return () => clearTimeout(timer);
    }, [memberSearchTerm, formData.linkType, formData.linkedId, showForm, searchMembers]);

    const openCreate = () => {
        setFormMode('create');
        setFormData(emptyForm());
        setMemberSearchTerm('');
        setMemberSearchResults([]);
        setShowMemberDropdown(false);
        setSelected(null);
        setShowForm(true);
    };

    const openEdit = (row) => {
        setFormMode('edit');
        setSelected(row);
        const linkType = row.SupermanMember ? 'SupermanMember' : 'godMember';
        const linkedId = row.SupermanMember?._id || row.SupermanMember || row.godMember?._id || row.godMember || '';
        const label = linkType === 'godMember'
            ? formatGodMemberLabel(row.godMember)
            : formatSupermanMemberLabel(row.SupermanMember);
        setFormData({
            linkType,
            linkedId,
            paymentDate: row.paymentDate ? new Date(row.paymentDate).toISOString().slice(0, 10) : '',
            paymentAmount: row.paymentAmount ?? '',
            paymentRemarks: row.paymentRemarks || '',
            reviewCount: String(row.reviewCount ?? 0),
            intervalDays: String(row.intervalDays ?? 7),
            startDate: row.startDate ? new Date(row.startDate).toISOString().slice(0, 10) : '',
            platforms: row.platforms?.length ? [...row.platforms] : ['godReview'],
            status: row.status || 'active',
        });
        setMemberSearchTerm(label);
        setMemberSearchResults([]);
        setShowMemberDropdown(false);
        setShowForm(true);
    };

    const openView = async (row) => {
        setSelected(row);
        setShowView(true);
        setViewLoading(true);
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/supermanpayments/${row._id}`,
                { headers: authHeaders() }
            );
            if (res.data.success) {
                setSelected(res.data.data);
                setDeliveries(res.data.data.deliveries || []);
            }
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setViewLoading(false);
        }
    };

    const buildPayload = () => {
        const payload = {
            paymentDate: formData.paymentDate || null,
            paymentAmount: formData.paymentAmount === '' ? 0 : Number(formData.paymentAmount),
            paymentRemarks: formData.paymentRemarks,
            reviewCount: formData.reviewCount === '' ? 0 : Number(formData.reviewCount),
            intervalDays: formData.intervalDays === '' ? 7 : Number(formData.intervalDays),
            startDate: formData.startDate || formData.paymentDate || null,
            platforms: cleanPlatforms(formData.platforms),
            status: formData.status,
            godMember: null,
            SupermanMember: null,
        };
        if (formData.linkType === 'godMember') payload.godMember = formData.linkedId || null;
        else payload.SupermanMember = formData.linkedId || null;
        return payload;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.linkedId) {
            setError('Please select a linked member');
            return;
        }
        setFormLoading(true);
        setError('');
        try {
            const payload = buildPayload();
            const request = formMode === 'edit'
                ? axios.put(`${process.env.REACT_APP_API_URL}/api/supermanpayments/${selected._id}`, payload, { headers: authHeaders() })
                : axios.post(`${process.env.REACT_APP_API_URL}/api/supermanpayments`, payload, { headers: authHeaders() });
            const res = await request;
            if (res.data.success) {
                setShowForm(false);
                setSelected(null);
                setMemberSearchTerm('');
                fetchDeliveryOptions();
                fetchItems(currentPage, searchTerm, memberTypeFilter, statusFilter);
            }
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setFormLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!selected) return;
        try {
            setDeleteLoading(true);
            await axios.delete(`${process.env.REACT_APP_API_URL}/api/supermanpayments/${selected._id}`, { headers: authHeaders() });
            setShowDelete(false);
            setSelected(null);
            fetchItems(currentPage, searchTerm, memberTypeFilter, statusFilter);
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setDeleteLoading(false);
        }
    };

    const openComplete = (delivery) => {
        setCompleteTarget(delivery);
        const senderId = delivery.sender?._id || delivery.sender || '';
        const senderDoc = delivery.sender?.name
            ? delivery.sender
            : (supermanArmies.find((a) => String(a._id) === String(senderId)) || null);
        const fallbackNames = selected?.platforms?.length ? selected.platforms : ['godReview'];
        setDeliveryForm({
            reviewComment: delivery.reviewComment || '',
            sender: senderId,
            type: delivery.type || 'Massage Review',
            platforms: toDeliveryPlatformRows(delivery.platforms, fallbackNames),
            remarks: delivery.remarks || '',
        });
        setSenderSearchTerm(senderDoc ? formatArmyLabel(senderDoc) : '');
        setShowSenderDropdown(false);
        setShowComplete(true);
    };

    const handleSenderSelect = (army) => {
        setDeliveryForm((prev) => ({ ...prev, sender: army._id }));
        setSenderSearchTerm(formatArmyLabel(army));
        setShowSenderDropdown(false);
    };

    const handleCompleteDelivery = async (e) => {
        e.preventDefault();
        if (!completeTarget) return;
        if (!deliveryForm.sender) {
            setError('Please select a Superman Army sender');
            return;
        }
        setCompleteLoading(true);
        setError('');
        const payload = {
            reviewComment: deliveryForm.reviewComment,
            sender: deliveryForm.sender || null,
            type: deliveryForm.type.trim() || 'Massage Review',
            platforms: cleanDeliveryPlatforms(deliveryForm.platforms),
            remarks: deliveryForm.remarks,
        };
        const isEditDone = completeTarget.status === 'done';
        try {
            const res = isEditDone
                ? await axios.put(
                    `${process.env.REACT_APP_API_URL}/api/supermandeliveries/${completeTarget._id}`,
                    { ...payload, status: 'done' },
                    { headers: authHeaders() }
                )
                : await axios.post(
                    `${process.env.REACT_APP_API_URL}/api/supermandeliveries/${completeTarget._id}/complete`,
                    payload,
                    { headers: authHeaders() }
                );
            if (res.data.success) {
                setShowComplete(false);
                setCompleteTarget(null);
                setSenderSearchTerm('');
                setShowSenderDropdown(false);
                fetchDeliveryOptions();
                if (selected?._id) await openView(selected);
                fetchItems(currentPage, searchTerm, memberTypeFilter, statusFilter);
            }
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setCompleteLoading(false);
        }
    };

    const handleMemberSelect = (member) => {
        const label = formData.linkType === 'godMember'
            ? formatGodMemberLabel(member)
            : formatSupermanMemberLabel(member);
        setFormData((prev) => ({ ...prev, linkedId: member._id }));
        setMemberSearchTerm(label);
        setMemberSearchResults([]);
        setShowMemberDropdown(false);
    };

    const updatePlatformAt = (listKey, index, value) => {
        if (listKey === 'form') {
            setFormData((prev) => {
                const next = [...prev.platforms];
                next[index] = value;
                return { ...prev, platforms: next };
            });
        } else {
            setDeliveryForm((prev) => {
                const next = [...prev.platforms];
                next[index] = { ...next[index], platform: value };
                return { ...prev, platforms: next };
            });
        }
    };

    const updateDeliveryPlatformDateTime = (index, value) => {
        setDeliveryForm((prev) => {
            const next = [...prev.platforms];
            next[index] = { ...next[index], dateTime: value };
            return { ...prev, platforms: next };
        });
    };

    const addPlatformRow = (listKey) => {
        if (listKey === 'form') {
            setFormData((prev) => ({ ...prev, platforms: [...prev.platforms, ''] }));
        } else {
            setDeliveryForm((prev) => ({
                ...prev,
                platforms: [...prev.platforms, { platform: '', dateTime: '' }],
            }));
        }
    };

    const removePlatformRow = (listKey, index) => {
        if (listKey === 'form') {
            setFormData((prev) => ({
                ...prev,
                platforms: prev.platforms.length <= 1
                    ? ['']
                    : prev.platforms.filter((_, i) => i !== index),
            }));
        } else {
            setDeliveryForm((prev) => ({
                ...prev,
                platforms: prev.platforms.length <= 1
                    ? [{ platform: '', dateTime: '' }]
                    : prev.platforms.filter((_, i) => i !== index),
            }));
        }
    };

    const renderPackagePlatformInputs = (platforms) => (
        <div className="space-y-2">
            {platforms.map((platform, index) => (
                <div key={`form-platform-${index}`} className="flex gap-2 items-center">
                    <input
                        type="text"
                        list="superman-platform-suggestions"
                        value={platform}
                        onChange={(e) => updatePlatformAt('form', index, e.target.value)}
                        placeholder="Type or select platform"
                        className={inputClass}
                        autoComplete="off"
                    />
                    {platforms.length > 1 && (
                        <button
                            type="button"
                            onClick={() => removePlatformRow('form', index)}
                            className="p-2 text-red-500 hover:text-red-700"
                            title="Remove platform"
                        >
                            <FiX size={18} />
                        </button>
                    )}
                </div>
            ))}
            <button
                type="button"
                onClick={() => addPlatformRow('form')}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
            >
                <FiPlus size={14} /> Add platform
            </button>
        </div>
    );

    const renderDeliveryPlatformInputs = (platforms) => (
        <div className="space-y-3">
            <p className="text-xs text-gray-500">
                Date &amp; time is optional. Leave empty if that platform is not posted yet — the review stays pending until every platform has a time.
            </p>
            {platforms.map((row, index) => (
                <div key={`delivery-platform-${index}`} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-end">
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Platform</label>
                        <input
                            type="text"
                            list="superman-platform-suggestions"
                            value={row.platform}
                            onChange={(e) => updatePlatformAt('delivery', index, e.target.value)}
                            placeholder="Type or select platform"
                            className={inputClass}
                            autoComplete="off"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Date & Time (SG, optional)</label>
                        <input
                            type="datetime-local"
                            value={row.dateTime || ''}
                            onChange={(e) => updateDeliveryPlatformDateTime(index, e.target.value)}
                            className={inputClass}
                        />
                    </div>
                    {platforms.length > 1 && (
                        <button
                            type="button"
                            onClick={() => removePlatformRow('delivery', index)}
                            className="p-2 text-red-500 hover:text-red-700 mb-1"
                            title="Remove platform"
                        >
                            <FiX size={18} />
                        </button>
                    )}
                </div>
            ))}
            <button
                type="button"
                onClick={() => addPlatformRow('delivery')}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
            >
                <FiPlus size={14} /> Add platform
            </button>
        </div>
    );

    const filteredSenders = (() => {
        const term = senderSearchTerm.trim();
        const list = !term || deliveryForm.sender
            ? supermanArmies
            : supermanArmies.filter((army) => armyMatchesSearch(army, term));
        return list.slice(0, 50);
    })();

    const doneCount = (row) => Math.max((row.reviewCount || 0) - (row.reviewBal || 0), 0);

    return (
        <Layout>
            <div className="px-2 w-full max-w-none p-6">
                <datalist id="superman-platform-suggestions">
                    {pastPlatforms.map((p) => (
                        <option key={p} value={p} />
                    ))}
                </datalist>
                <datalist id="superman-review-type-suggestions">
                    {reviewTypes.map((reviewType) => (
                        <option key={reviewType} value={reviewType} />
                    ))}
                </datalist>
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-2xl font-bold">Superman Payments</h1>
                        <p className="text-gray-600">Total: {total} packages</p>
                    </div>
                    <button onClick={openCreate} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center">
                        <FiPlus className="mr-2" />
                        Create Package
                    </button>
                </div>

                <div className="mb-6 flex flex-wrap gap-2">
                    <div className="relative flex-1 min-w-[200px]">
                        <input
                            type="text"
                            className="w-full px-4 py-2 border border-gray-300 rounded-md text-blue-700 font-bold"
                            placeholder="Search member name / godID / webID / remarks..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && (setSearchTerm(searchInput), setCurrentPage(1))}
                        />
                        <FiSearch className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    </div>
                    <select
                        value={memberTypeFilter}
                        onChange={(e) => { setMemberTypeFilter(e.target.value); setCurrentPage(1); }}
                        className="px-3 py-2 border border-gray-300 rounded-md"
                    >
                        <option value="">All member links</option>
                        <option value="godMember">God Member</option>
                        <option value="SupermanMember">Superman Member</option>
                    </select>
                    <select
                        value={statusFilter}
                        onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                        className="px-3 py-2 border border-gray-300 rounded-md"
                    >
                        <option value="">All statuses</option>
                        <option value="active">Active</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                    </select>
                    <button onClick={() => { setSearchTerm(searchInput); setCurrentPage(1); }} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">Search</button>
                    <button
                        onClick={() => {
                            setSearchInput('');
                            setSearchTerm('');
                            setMemberTypeFilter('');
                            setStatusFilter('');
                            setCurrentPage(1);
                        }}
                        className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                    >
                        Clear
                    </button>
                </div>

                {error && <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">{error}</div>}

                <div className="bg-white rounded-lg shadow overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Member</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paid</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Progress</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Next Due</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Interval</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {loading ? (
                                <tr><td colSpan={8} className="px-4 py-6 text-center">Loading...</td></tr>
                            ) : items.length === 0 ? (
                                <tr><td colSpan={8} className="px-4 py-6 text-center">No payments found</td></tr>
                            ) : (
                                items.map((row) => (
                                    <tr key={row._id} className={paymentRowClass(row)}>
                                        <td className="px-4 py-3 text-sm text-blue-700">{formatLinkedMember(row)}</td>
                                        <td className="px-4 py-3 text-sm">{formatSingaporeDateDmy(row.paymentDate)}</td>
                                        <td className="px-4 py-3 text-sm text-right">{Number(row.paymentAmount || 0).toFixed(2)}</td>
                                        <td className="px-4 py-3 text-sm text-center font-semibold">
                                            {doneCount(row)}/{row.reviewCount ?? 0}
                                            <span className="text-gray-500 font-normal ml-1">({row.reviewBal ?? 0} left)</span>
                                        </td>
                                        <td className="px-4 py-3 text-sm">{formatSingaporeDateDmy(row.nextReviewDate)}</td>
                                        <td className={`px-4 py-3 text-sm capitalize ${statusClass(row.status)}`}>{row.status || 'active'}</td>
                                        <td className="px-4 py-3 text-sm">{row.intervalDays || 7}d</td>
                                        <td className="px-4 py-3 text-right space-x-1">
                                            <button onClick={() => openView(row)} className="p-1.5 text-blue-600 hover:bg-blue-100 rounded-full" title="View / Deliveries"><FiEye /></button>
                                            <button onClick={() => openEdit(row)} className="p-1.5 text-green-600 hover:bg-green-100 rounded-full" title="Edit"><FiEdit2 /></button>
                                            <button onClick={() => { setSelected(row); setShowDelete(true); }} className="p-1.5 text-red-600 hover:bg-red-100 rounded-full" title="Delete"><FiTrash2 /></button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />

                {showForm && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                        <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-xl font-bold">{formMode === 'edit' ? 'Edit Package' : 'Create Package'}</h2>
                                <button onClick={() => setShowForm(false)} className="text-gray-500 hover:text-gray-700"><FiX size={20} /></button>
                            </div>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Link Type</label>
                                        <select
                                            value={formData.linkType}
                                            onChange={(e) => {
                                                setFormData((prev) => ({ ...prev, linkType: e.target.value, linkedId: '' }));
                                                setMemberSearchTerm('');
                                                setMemberSearchResults([]);
                                                setShowMemberDropdown(false);
                                            }}
                                            className={inputClass}
                                        >
                                            <option value="SupermanMember">Superman Member</option>
                                            <option value="godMember">God Member</option>
                                        </select>
                                    </div>
                                    <div className="relative">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Member</label>
                                        <input
                                            type="text"
                                            value={memberSearchTerm}
                                            onChange={(e) => {
                                                setMemberSearchTerm(e.target.value);
                                                setShowMemberDropdown(true);
                                                setFormData((prev) => ({ ...prev, linkedId: '' }));
                                            }}
                                            onFocus={() => {
                                                if (!formData.linkedId) setShowMemberDropdown(true);
                                            }}
                                            placeholder={formData.linkType === 'godMember'
                                                ? 'Search by godID / godName / webID...'
                                                : 'Search by name / webID...'}
                                            className={inputClass}
                                            autoComplete="off"
                                        />
                                        {showMemberDropdown && !formData.linkedId && (
                                            <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                                                {memberSearchLoading ? (
                                                    <div className="px-3 py-2 text-sm text-gray-500">Searching...</div>
                                                ) : !memberSearchTerm.trim() ? (
                                                    <div className="px-3 py-2 text-sm text-gray-500">Type to search members</div>
                                                ) : memberSearchResults.length === 0 ? (
                                                    <div className="px-3 py-2 text-sm text-gray-500">No members found</div>
                                                ) : (
                                                    memberSearchResults.map((member) => (
                                                        <div
                                                            key={member._id}
                                                            onClick={() => handleMemberSelect(member)}
                                                            className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm text-gray-900"
                                                        >
                                                            {formData.linkType === 'godMember'
                                                                ? formatGodMemberLabel(member)
                                                                : formatSupermanMemberLabel(member)}
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        )}
                                        {formData.linkedId && (
                                            <p className="text-xs text-green-700 mt-1">Member selected</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date</label>
                                        <input type="date" value={formData.paymentDate} onChange={(e) => setFormData((p) => ({ ...p, paymentDate: e.target.value }))} className={inputClass} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Start Date (1st review)</label>
                                        <input type="date" value={formData.startDate} onChange={(e) => setFormData((p) => ({ ...p, startDate: e.target.value }))} className={inputClass} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Payment Amount</label>
                                        <input type="number" step="0.01" value={formData.paymentAmount} onChange={(e) => setFormData((p) => ({ ...p, paymentAmount: e.target.value }))} className={inputClass} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Review Count (purchased)</label>
                                        <input type="number" min="0" step="1" value={formData.reviewCount} onChange={(e) => setFormData((p) => ({ ...p, reviewCount: e.target.value }))} className={inputClass} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Interval Days</label>
                                        <input type="number" min="1" step="1" value={formData.intervalDays} onChange={(e) => setFormData((p) => ({ ...p, intervalDays: e.target.value }))} className={inputClass} />
                                        <p className="text-xs text-gray-500 mt-1">e.g. 7 = one review per week</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                        <select value={formData.status} onChange={(e) => setFormData((p) => ({ ...p, status: e.target.value }))} className={inputClass}>
                                            <option value="active">Active</option>
                                            <option value="completed">Completed</option>
                                            <option value="cancelled">Cancelled</option>
                                        </select>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Default Platforms</label>
                                    {renderPackagePlatformInputs(formData.platforms)}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Remarks</label>
                                    <textarea rows={3} value={formData.paymentRemarks} onChange={(e) => setFormData((p) => ({ ...p, paymentRemarks: e.target.value }))} className={inputClass} />
                                </div>
                                <div className="flex justify-end gap-3">
                                    <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border border-gray-300 rounded-md">Cancel</button>
                                    <button type="submit" disabled={formLoading} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
                                        {formLoading ? 'Saving...' : formMode === 'edit' ? 'Update' : 'Create'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {showView && selected && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                        <div className="bg-white rounded-lg shadow-xl w-full max-w-[95vw] xl:max-w-[1400px] p-6 max-h-[90vh] overflow-y-auto space-y-4">
                            <div className="flex justify-between items-center">
                                <h2 className="text-lg font-bold">Package + Deliveries</h2>
                                <button onClick={() => { setShowView(false); setSelected(null); setDeliveries([]); }} className="text-gray-500"><FiX size={20} /></button>
                            </div>
                            {viewLoading ? (
                                <p className="text-gray-500">Loading...</p>
                            ) : (
                                <>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                                        <p><span className="text-gray-600">Member:</span> <span className="text-blue-700">{formatLinkedMember(selected)}</span></p>
                                        <p><span className="text-gray-600">Amount:</span> {Number(selected.paymentAmount || 0).toFixed(2)}</p>
                                        <p><span className="text-gray-600">Progress:</span> {doneCount(selected)}/{selected.reviewCount ?? 0} ({selected.reviewBal ?? 0} left)</p>
                                        <p><span className="text-gray-600">Next due:</span> {formatSingaporeDateDmy(selected.nextReviewDate)}</p>
                                        <p><span className="text-gray-600">Interval:</span> every {selected.intervalDays || 7} days</p>
                                        <p><span className="text-gray-600">Status:</span> <span className={`capitalize ${statusClass(selected.status)}`}>{selected.status}</span></p>
                                        <p className="md:col-span-2"><span className="text-gray-600">Platforms:</span> {(selected.platforms || []).join(', ') || '—'}</p>
                                        <p className="md:col-span-2"><span className="text-gray-600">Remarks:</span> {selected.paymentRemarks || '—'}</p>
                                    </div>

                                    <div className="border-t pt-4">
                                        <h3 className="font-semibold mb-3">Review Deliveries</h3>
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full text-sm">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th className="px-3 py-2 text-left">#</th>
                                                        <th className="px-3 py-2 text-left">Scheduled</th>
                                                        <th className="px-3 py-2 text-left">Status</th>
                                                        <th className="px-3 py-2 text-left">Sender</th>
                                                        <th className="px-3 py-2 text-left">Type</th>
                                                        <th className="px-3 py-2 text-left">Platforms (date/time)</th>
                                                        <th className="px-3 py-2 text-left">Comment</th>
                                                        <th className="px-3 py-2 text-right">Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y">
                                                    {deliveries.length === 0 ? (
                                                        <tr><td colSpan={8} className="px-3 py-4 text-center text-gray-500">No deliveries</td></tr>
                                                    ) : deliveries.map((d) => {
                                                        const editValue = getDeliveryEditValue(d);
                                                        return (
                                                        <tr key={d._id} className={deliveryRowClass(d)}>
                                                            <td className="px-3 py-2">{d.sequence}</td>
                                                            <td className="px-3 py-2">{formatSingaporeDateDmy(d.scheduledAt)}</td>
                                                            <td className="px-3 py-2 capitalize">{d.status}</td>
                                                            <td className="px-3 py-2">{formatArmyLabel(d.sender)}</td>
                                                            <td className="px-3 py-2">{d.type || 'Massage Review'}</td>
                                                            <td className="px-3 py-2 min-w-[260px] text-xs leading-5">
                                                                {(() => {
                                                                    const text = formatDeliveryPlatforms(d.platforms);
                                                                    if (text === '—') return '—';
                                                                    return text.split('\n').map((line, i) => (
                                                                        <div key={`${d._id}-platform-${i}`} className="whitespace-nowrap">
                                                                            {line}
                                                                        </div>
                                                                    ));
                                                                })()}
                                                            </td>
                                                            <td className="px-3 py-2 max-w-[220px] truncate">{d.reviewComment || '—'}</td>
                                                            <td className="px-3 py-2 text-right">
                                                                {selected.status !== 'cancelled' && (
                                                                    <div className="inline-flex items-center gap-2 justify-end">
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => openComplete(d)}
                                                                            className="inline-flex items-center gap-1 px-2 py-1 text-blue-700 bg-blue-50 hover:bg-blue-100 rounded"
                                                                        >
                                                                            <FiEdit2 /> Edit
                                                                        </button>
                                                                        {editValue ? (
                                                                            <span className={`text-sm ${deliveryEditStatusClass(editValue)}`}>
                                                                                {editValue}
                                                                            </span>
                                                                        ) : null}
                                                                    </div>
                                                                )}
                                                            </td>
                                                        </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {showComplete && completeTarget && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
                        <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-lg font-bold">
                                    {completeTarget.status === 'done'
                                        ? `Edit Review #${completeTarget.sequence}`
                                        : `Save Review #${completeTarget.sequence}`}
                                </h2>
                                <button onClick={() => setShowComplete(false)} className="text-gray-500"><FiX size={20} /></button>
                            </div>
                            <form onSubmit={handleCompleteDelivery} className="space-y-4">
                                <div className="relative">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Sender</label>
                                    <input
                                        type="text"
                                        value={senderSearchTerm}
                                        onChange={(e) => {
                                            setSenderSearchTerm(e.target.value);
                                            setShowSenderDropdown(true);
                                            setDeliveryForm((prev) => ({ ...prev, sender: '' }));
                                        }}
                                        onFocus={() => {
                                            if (!deliveryForm.sender) setShowSenderDropdown(true);
                                        }}
                                        placeholder="Search by name / @username / TG ID..."
                                        className={inputClass}
                                        autoComplete="off"
                                        required={!deliveryForm.sender}
                                    />
                                    {showSenderDropdown && !deliveryForm.sender && (
                                        <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                                            {!senderSearchTerm.trim() && filteredSenders.length === 0 ? (
                                                <div className="px-3 py-2 text-sm text-gray-500">No army senders yet</div>
                                            ) : filteredSenders.length === 0 ? (
                                                <div className="px-3 py-2 text-sm text-gray-500">No senders found</div>
                                            ) : (
                                                filteredSenders.map((army) => (
                                                    <div
                                                        key={army._id}
                                                        onClick={() => handleSenderSelect(army)}
                                                        className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm text-gray-900"
                                                    >
                                                        {formatArmyLabel(army)}
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    )}
                                    {deliveryForm.sender && (
                                        <p className="text-xs text-green-700 mt-1">Sender selected</p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Review Type</label>
                                    <input
                                        type="text"
                                        list="superman-review-type-suggestions"
                                        value={deliveryForm.type}
                                        onChange={(e) => setDeliveryForm((p) => ({
                                            ...p,
                                            type: e.target.value,
                                        }))}
                                        placeholder="Select existing or type new"
                                        className={inputClass}
                                        autoComplete="off"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Platforms (each with its own date & time)
                                    </label>
                                    {renderDeliveryPlatformInputs(deliveryForm.platforms)}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Review Comment</label>
                                    <textarea
                                        rows={4}
                                        value={deliveryForm.reviewComment}
                                        onChange={(e) => setDeliveryForm((p) => ({ ...p, reviewComment: e.target.value }))}
                                        className={inputClass}
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                                    <textarea
                                        rows={2}
                                        value={deliveryForm.remarks}
                                        onChange={(e) => setDeliveryForm((p) => ({ ...p, remarks: e.target.value }))}
                                        className={inputClass}
                                    />
                                </div>
                                <div className="flex justify-end gap-3">
                                    <button type="button" onClick={() => setShowComplete(false)} className="px-4 py-2 border border-gray-300 rounded-md">Cancel</button>
                                    <button
                                        type="submit"
                                        disabled={completeLoading}
                                        className={`px-4 py-2 text-white rounded-md disabled:opacity-50 ${
                                            completeTarget.status === 'done'
                                                ? 'bg-blue-600 hover:bg-blue-700'
                                                : 'bg-emerald-600 hover:bg-emerald-700'
                                        }`}
                                    >
                                        {completeLoading
                                            ? 'Saving...'
                                            : (areDeliveryPlatformsComplete(deliveryForm.platforms)
                                                ? (completeTarget.status === 'done' ? 'Update (Done)' : 'Mark Done')
                                                : 'Save (Pending)')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {showDelete && selected && (
                    <DeleteConfirmationModal
                        title="Delete Package"
                        message="Delete this Superman payment package and all its deliveries?"
                        itemName={formatLinkedMember(selected)}
                        isLoading={deleteLoading}
                        onConfirm={handleDelete}
                        onCancel={() => { setShowDelete(false); setSelected(null); }}
                    />
                )}
            </div>
        </Layout>
    );
};

export default SupermanPaymentPage;
